import { useCallback } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'

import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import noop from 'lib/function/noop'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import StructureLabel from 'LuxKit/Label/StructureLabel'

const Root = styled(Clickable)`
  height: ${rem(44)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: background-color 0.2s;
  position: relative;
  overflow: visible;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: ${rem(12)};
  padding-top: ${rem(4)};

  &.selected {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.in-range-date {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &:disabled {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  &:not(:disabled):not(.selected) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
        color: ${props => props.theme.palette.neutral.surface.lightContrast};
      }
    }
    &:focus {
      background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
      color: ${props => props.theme.palette.neutral.surface.lightContrast};
    }
  }
`

const FeaturedIcon = styled(SolidFireIcon)`
  position: absolute;
  top: 0;
  right: ${rem(2)};
`

const PriceCaption = styled(Caption)`
  &:empty {
    display: none;
  }
`

const Label = styled(StructureLabel)`
  position: absolute;
  top: ${rem(-18)};
  left: ${rem(10)};
  z-index: 1;
`

interface Props extends Utils.PropsWithDataAttributes {
  day: Date

  selected?: boolean
  disabled?: boolean
  loading?: boolean
  isInRange?: boolean
  price?: number
  onClick?: (day: Date) => void
  cheapest?: boolean
  featured?: boolean
  label?: string
}

function FlightsDatePickerDay(props: Props) {
  const {
    day,
    loading,
    price,
    disabled,
    selected,
    isInRange,
    featured,
    cheapest,
    onClick = noop,
    label,
    ...rest
  } = props

  const onSelectDay = useCallback(() => {
    onClick(day)
  }, [day, onClick])

  return (
    <Root
      {...rest}
      onClick={onSelectDay}
      disabled={disabled}
      className={clsx({
        selected: selected && !loading,
        'in-range-date': isInRange && !loading,
      })}
    >
      {label && <Label variant="dark">
        {label}
      </Label>}
      {featured && <FeaturedIcon colour="urgency" size="XS" />}
      <BodyText variant="medium">
        {day.getDate()}
      </BodyText>
      <PriceCaption variant="smallalt" tabletVariant="medium" colour={cheapest ? 'highlight-primary-dark' : 'light-foreground-alt'}>
        {loading && !disabled && <TextLoadingBox width="80%" />}
        {!loading && !!price && !disabled && <FormatCurrency value={price} />}
      </PriceCaption>
    </Root>
  )
}

export default FlightsDatePickerDay
